<!--
 @description Accessible dialog wrapper built on the native `<dialog>` element.
 Supports three decorative shapes (chopped, pentagon, hexagon), an optional
 backdrop blur, full-screen mode, and click-outside-to-close behaviour. The
 parent controls open/close state via `isOpen`; the component emits `close`
 when dismissed. Prefer this over custom overlay divs for any modal content.

 @example
 <Modal :isOpen="showModal" shape="pentagon" @close="showModal = false">
   <p>Modal body content.</p>
 </Modal>

 @accessibility Uses the native `<dialog>` element — `showModal()` is called
 programmatically, which sets `aria-modal` and manages focus automatically.
 Ensure the first focusable element inside the slot is meaningful. Do not nest
 modals; use separate `isOpen` flags instead.

 For destructive confirmation dialogs, pass `dialogRole="alertdialog"` so
 assistive technologies announce the dialog as requiring an explicit user choice
 (AC-20, C-08). The default `"dialog"` role is preserved for all existing usages.
-->
<template>
  <dialog
    ref="modal"
    class="modal"
    :class="[fullScreen ? 'full-screen' : shape, { blurred: blurred }]"
    :role="dialogRole"
  >
    <i v-if="isClosable" class="modal__close fi fi-br-cross" @click="close"></i>
    <slot />
  </dialog>
</template>

<script setup lang="ts">
import { SHAPES } from '@/components/common/Global/modal/Shapes'
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
  /** When true, a close icon (×) is rendered in the top-right corner of the dialog. */
  isClosable: {
    type: Boolean,
    default: true
  },
  /** Drives `showModal()` / `close()` on the native dialog element. The parent must keep this in sync with the `close` event. */
  isOpen: {
    type: Boolean,
    default: false
  },
  /** When true, clicking outside the dialog boundary closes it and emits `close`. */
  isOutsideClosable: {
    type: Boolean,
    default: true
  },
  /** Decorative clip-path shape applied to the dialog. One of `SHAPES.CHOPPED`, `SHAPES.PENTAGON`, or `SHAPES.HEXAGON`. Ignored when `fullScreen` is true. */
  shape: {
    type: String,
    default: SHAPES.CHOPPED
  },
  /** Applies a CSS backdrop-blur class to the dialog background. */
  blurred: {
    type: Boolean,
    default: true
  },
  /** When true, the dialog expands to fill the viewport and `shape` is ignored. Use on mobile or for content-heavy overlays. */
  fullScreen: {
    type: Boolean,
    default: false
  },
  /**
   * ARIA role for the dialog element. Use `"alertdialog"` for destructive
   * confirmation dialogs (e.g. sign-out confirmation) so assistive technologies
   * announce the dialog as requiring an explicit user choice (AC-20, C-08).
   * Defaults to `"dialog"` — all existing usages are unaffected.
   */
  dialogRole: {
    type: String as () => 'dialog' | 'alertdialog',
    default: 'dialog'
  }
})

const emit = defineEmits(['close'])

const modal = ref<HTMLDialogElement | null>(null)

const handleOutsideClick = (e: MouseEvent) => {
  if (!props.isOutsideClosable) return
  const dialogDimentions = modal.value?.getBoundingClientRect()
  if (dialogDimentions === undefined) return
  if (
    e.clientX < dialogDimentions.left ||
    e.clientX > dialogDimentions.right ||
    e.clientY < dialogDimentions.top ||
    e.clientY > dialogDimentions.bottom
  ) {
    modal.value?.close()
    emit('close')
  }
}

// `cancel` fires when the user dismisses the dialog with Escape. Native `<dialog>`
// already closes itself; we only need to notify the parent so `isOpen` stays in sync.
const handleCancel = () => {
  emit('close')
}

onMounted(() => {
  if (props.isOpen) {
    modal.value?.showModal()
  }

  modal.value?.addEventListener('click', handleOutsideClick)
  modal.value?.addEventListener('cancel', handleCancel)
})

onBeforeUnmount(() => {
  modal.value?.removeEventListener('click', handleOutsideClick)
  modal.value?.removeEventListener('cancel', handleCancel)
})

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      modal.value?.showModal()
    } else {
      modal.value?.close()
    }
  }
)

const close = () => {
  modal.value?.close()
  emit('close')
}
</script>

<style lang="scss" scoped>
.modal {
  width: fit-content;
  height: fit-content;
  min-width: 100px;
  min-height: 100px;
  border: 1px solid transparent;
  padding: $base-spacing;
  // Use a filter-based drop-shadow rather than box-shadow: the shape modifiers
  // below apply a clip-path, and clip-path clips box-shadow away entirely.
  // drop-shadow follows the clipped silhouette, so the shadow renders for the
  // chopped / pentagon / hexagon shapes too.
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25));
  background-color: var(--white);
  color: var(--text);

  &:focus-visible {
    outline: none;
  }

  &__close {
    position: absolute;
    top: 70px;
    right: 30px;
    cursor: pointer;
    z-index: 2;
    color: var(--text);

    &:hover {
      color: var(--primary-dark);
    }
  }

  // Shape modifiers — values driven by the `shape` prop via SHAPES enum.
  // Kept as plain classes (not BEM modifiers) because they are prop-value
  // strings shared with Shapes.ts and consumed by external callers.
  &.chopped {
    clip-path: $shape-chopped;
    max-height: 80vh;

    .modal__close {
      top: 40px;
      right: 35px;
    }
  }

  &.pentagon {
    clip-path: $shape-pentagon;
  }

  &.hexagon {
    clip-path: $shape-hexagon;
  }

  // Backdrop blur — driven by the `blurred` prop; kept as plain class
  // because callers bind it via the prop interface.
  &.blurred {
    &::backdrop {
      background: rgba(255, 255, 255, 0.25);
      box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.18);
    }
  }

  // Full-screen variant — driven by the `fullScreen` prop.
  &.full-screen {
    width: 100vw;
    min-height: -webkit-fill-available;
    min-height: 100dvh;
    position: fixed;
    top: 0;
    left: 0;
    background-color: var(--white);
    background-color: rgba(255, 255, 255, 0.95);
    // Fills the viewport — a drop-shadow would only add an off-screen repaint.
    filter: none;
  }
}
</style>
